<template>
  <div>
    <!-- Time Doctor Hours Summary -->
    <v-card elevation="0" class="mb-4">
      <v-card-title>
        <v-icon left color="primary">mdi-clock-check-outline</v-icon>
        <span style="font-size: 1.3rem; font-weight: 600">
          Your Tracked Hours
        </span>
        <v-spacer></v-spacer>
        <template v-if="timeDoctorAuthorized">
          <v-chip color="primary" outlined class="mr-2">
            <v-icon left small>mdi-sigma</v-icon>
            Total: {{ totalTimeDoctorHours }}
          </v-chip>
          <v-btn small outlined color="primary" @click="fetchTimeDoctorData" :loading="isLoadingTimeDoctorData"
            class="text-none mr-2">
            <v-icon left small>mdi-refresh</v-icon>
            Refresh
          </v-btn>
          <!-- <v-btn small text color="error" @click="disconnectTimeDoctor" class="text-none">
            <v-icon left small>mdi-link-off</v-icon>
            Disconnect
          </v-btn> -->
        </template>
      </v-card-title>
      <v-card-subtitle>
        <span style="font-size: 0.8rem; font-weight: 500">
          Time Doctor Classic + Time Doctor 2 + DGTasks hours for the selected payroll period (overlapping time counted once)
        </span>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <!-- Authorization Required - Admin must connect -->
        <div v-if="!timeDoctorAuthorized && !isCheckingAuth" class="text-center py-6">
          <v-icon size="64" color="grey lighten-1">mdi-link-variant-off</v-icon>
          <div class="mt-4 text-h6 grey--text">Time Doctor Not Connected</div>
          <div class="mt-2 text-body-2 grey--text mb-4">
            Please contact your administrator to connect Time Doctor integration
          </div>
        </div>

        <!-- Loading Auth Status -->
        <div v-else-if="isCheckingAuth" class="text-center py-6">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-4">Checking authorization status...</div>
        </div>

        <!-- Authorized Content -->
        <template v-else>
          <!-- Date Range Selector -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-menu v-model="timeDoctorDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="formattedTimeDoctorDateRange" label="Payroll Period" outlined dense readonly
                    v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-range" clearable
                    @click:clear="clearTimeDoctorDateRange" hint="Select the date range to fetch your tracked hours"
                    persistent-hint></v-text-field>
                </template>
                <v-date-picker v-model="timeDoctorDateRange" range no-title scrollable
                  @change="onTimeDoctorDateChange"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-chip-group>
                <v-chip small outlined @click="setCurrentPayrollPeriod" color="primary">
                  <v-icon left small>mdi-calendar-today</v-icon>
                  Current Period
                </v-chip>
                <v-chip small outlined @click="setPreviousPayrollPeriod">
                  <v-icon left small>mdi-calendar-arrow-left</v-icon>
                  Previous Period
                </v-chip>
              </v-chip-group>
            </v-col>
          </v-row>

          <v-progress-linear v-if="isLoadingTimeDoctorData" indeterminate color="primary"></v-progress-linear>
          
          <!-- Error message -->
          <v-alert v-else-if="timeDoctorError" type="error" dense outlined class="mb-4">
            {{ timeDoctorError }}
            <template v-if="timeDoctorError.includes('authorization')">
              <span class="text-caption ml-2">(Contact admin to re-authorize)</span>
            </template>
          </v-alert>
          
          <!-- No data message -->
          <div v-else-if="!timeDoctorData || !timeDoctorData.items || timeDoctorData.items.length === 0"
            class="text-center py-4 grey--text">
            <v-icon large color="grey lighten-1">mdi-clock-outline</v-icon>
            <div class="mt-2">No tracked hours found for the selected period</div>
            <div class="text-caption">Select a date range and click Refresh to fetch data</div>
          </div>
          
          <!-- Time Doctor Data Table -->
          <div v-else>
            <!-- Summary -->
            <v-alert type="info" dense outlined class="mb-4">
              <v-row no-gutters align="center">
                <v-col>
                  <strong>Period:</strong> {{ formatDate(timeDoctorData.period?.start) }} - {{
                    formatDate(timeDoctorData.period?.end) }}
                </v-col>
                <v-col class="text-right">
                  <strong>Total Time:</strong>
                  <v-chip small color="primary" class="ml-2">
                    {{ timeDoctorData.total_formatted || '0h 00m' }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-alert>

            <!-- Worklogs Table -->
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Project</th>
                    <th class="text-left">Task</th>
                    <th class="text-left">User</th>
                    <th class="text-right">Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, i) in timeDoctorData.items" :key="i">
                    <td>
                      <v-chip x-small outlined color="blue">{{ entry.project_name || 'Unknown' }}</v-chip>
                      <v-chip v-if="entry.source" x-small label class="ml-1" :color="sourceColor(entry.source)">
                        {{ sourceLabel(entry.source) }}
                      </v-chip>
                    </td>
                    <td>{{ entry.task_name || 'Unknown Task' }}</td>
                    <td>{{ entry.user_name || '-' }}</td>
                    <td class="text-right">
                      <strong>{{ entry.formatted_time || `${entry.hours}h ${entry.minutes}m` }}</strong>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="grey lighten-4">
                    <td colspan="3" class="text-right font-weight-bold">Total:</td>
                    <td class="text-right font-weight-bold">
                      {{ timeDoctorData.total_formatted || '0h 00m' }}
                    </td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>

            <div class="text-caption grey--text mt-2">
              Showing {{ timeDoctorData.items_count || 0 }} worklog entries
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>

    <!-- Payroll Submission and History Tabs -->
    <v-card elevation="0">
      <v-tabs v-model="activeTab" color="primary">
        <v-tab>
          <v-icon left>mdi-file-document-edit-outline</v-icon>
          Payroll Submission
        </v-tab>
        <v-tab>
          <v-icon left>mdi-history</v-icon>
          Submissions History
        </v-tab>
        <!-- <v-tab>
          <v-icon left>mdi-alert-circle-outline</v-icon>
          Exceptions
          <v-badge v-if="totalExceptionsCount > 0" :content="totalExceptionsCount" color="error" inline class="ml-1"></v-badge>
        </v-tab> -->
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- Tab 1: Payroll Submission Form -->
        <v-tab-item>
          <!-- Success Confirmation -->
          <v-card-text v-if="submissionSuccess" class="text-center py-8">
            <v-icon size="80" color="success">mdi-check-circle</v-icon>
            <div class="text-h5 mt-4 success--text">Payroll Submitted Successfully!</div>
            <div class="text-body-1 grey--text mt-2 mb-6">
              Your payroll submission has been received and is pending review.
            </div>
            <v-btn color="primary" @click="startNewSubmission" class="text-none">
              <v-icon left small>mdi-plus</v-icon>
              Submit Another Payroll
            </v-btn>
          </v-card-text>
          
          <!-- Submission Form -->
          <v-card-text v-else-if="!isLoadingTimeDoctorData && !isCheckingAuth">
            <v-form ref="payrollForm" v-model="formValid" lazy-validation>
              <v-row>
                <!-- Email (prefilled) -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="formData.email" label="Email *" outlined dense readonly
                    :rules="[rules.required, rules.email]" prepend-inner-icon="mdi-email"></v-text-field>
                </v-col>

                <!-- Name (prefilled) -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="formData.name" label="Name *" outlined dense readonly :rules="[rules.required]"
                    prepend-inner-icon="mdi-account"></v-text-field>
                </v-col>

                <!-- Invoice Service Period -->
                <v-col cols="12" md="6">
                  <v-menu v-model="dateRangeMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                    min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="formattedDateRange" label="Invoice Service Period *" outlined dense readonly
                        v-bind="attrs" v-on="on" :rules="[rules.required]" prepend-inner-icon="mdi-calendar-range" clearable
                        @click:clear="clearDateRange"></v-text-field>
                    </template>
                    <v-date-picker v-model="formData.invoicePeriod" range no-title scrollable
                      @change="onInvoicePeriodChange"></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Company -->
                <v-col cols="12" md="6">
                  <v-autocomplete v-model="formData.company" :items="companies" item-text="name" item-value="id"
                    label="Company *" outlined dense return-object :rules="[rules.required]"
                    prepend-inner-icon="mdi-domain"></v-autocomplete>
                </v-col>

                <!-- Invoice Number -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="formData.invoiceNumber" label="Invoice Number *" outlined dense
                    :rules="[rules.required]" prepend-inner-icon="mdi-file-document"
                    placeholder="e.g., INV-2024-001"></v-text-field>
                </v-col>

                <!-- Hours Worked -->
                <v-col cols="12" md="6">
                  <v-row no-gutters>
                    <v-col cols="6" class="pr-2">
                      <v-text-field v-model.number="formData.hoursWorked.hours" label="Hours *" outlined dense type="number"
                        min="0" max="999" :rules="[rules.required, rules.positiveNumber]"
                        prepend-inner-icon="mdi-clock-outline"></v-text-field>
                    </v-col>
                    <v-col cols="6" class="pl-2">
                      <v-text-field v-model.number="formData.hoursWorked.minutes" label="Minutes *" outlined dense
                        type="number" min="0" max="59" :rules="[rules.required, rules.minutes]"></v-text-field>
                    </v-col>
                  </v-row>
                  <!-- Hours Override Warning -->
                  <v-alert v-if="hoursOverridden" type="warning" dense text class="mt-1 mb-0">
                    <div class="d-flex align-center">
                      <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                      <span class="text-caption">
                        Hours differ from Time Doctor ({{ totalTimeDoctorHours }}) by 
                        <strong>{{ hoursVariancePercent > 0 ? '+' : '' }}{{ hoursVariancePercent.toFixed(1) }}%</strong>
                      </span>
                    </div>
                  </v-alert>
                </v-col>

                <!-- Invoice Amount with Currency -->
                <v-col cols="12" md="6">
                  <v-row no-gutters>
                    <v-col cols="4" class="pr-2">
                      <v-select v-model="formData.currency" :items="currencies" label="Currency *" outlined dense
                        :rules="[rules.required]"></v-select>
                    </v-col>
                    <v-col cols="8" class="pl-2">
                      <v-text-field v-model.number="formData.invoiceAmount" label="Invoice Amount *" outlined dense
                        type="number" min="0" step="0.01" :rules="[rules.required, rules.positiveNumber]"
                        prepend-inner-icon="mdi-cash"
                        :hint="calculatedAmountFormatted ? `Calculated: ${calculatedAmountFormatted}` : null"
                        :persistent-hint="!!calculatedAmountFormatted"></v-text-field>
                    </v-col>
                  </v-row>
                  <!-- Amount Mismatch Warning -->
                  <v-alert v-if="amountMismatch" type="warning" dense text class="mt-1 mb-0">
                    <div class="d-flex align-center">
                      <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                      <span class="text-caption">
                        Invoice amount differs from calculated ({{ calculatedAmountFormatted }}) by 
                        <strong>{{ amountVariancePercent > 0 ? '+' : '' }}{{ amountVariancePercent.toFixed(1) }}%</strong>
                      </span>
                    </div>
                  </v-alert>
                  <!-- No Hourly Rate Warning -->
                  <v-alert v-else-if="!user?.hourly_rate" type="info" dense text class="mt-1 mb-0">
                    <span class="text-caption">
                      <v-icon small color="info" class="mr-1">mdi-information</v-icon>
                      No hourly rate configured. Amount validation unavailable.
                    </span>
                  </v-alert>
                </v-col>

                <!-- Invoice File (PDF) -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="formData.invoiceFile" label="Invoice (PDF) *" outlined dense accept=".pdf"
                    :rules="[rules.required, rules.pdfFile]" prepend-icon="" prepend-inner-icon="mdi-file-pdf-box"
                    show-size></v-file-input>
                </v-col>

                <!-- Certificate of Registration -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="formData.corFile" :label="hasPriorCor ? 'Copy of Certificate of Registration (Already on file)' : 'Copy of Certificate of Registration *'" outlined dense
                    :rules="hasPriorCor ? [] : [rules.required]" prepend-icon="" prepend-inner-icon="mdi-certificate" show-size></v-file-input>
                  
                  <v-alert v-if="hasPriorCor" type="success" dense text class="mt-2 mb-2">
                    <div class="d-flex align-center">
                      <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                      <span class="text-caption">COR already on file from a previous submission. Upload only if you want to update it.</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-1">
                        If you are not a registered freelancer / self employed person we recommend you start the process. It's a very easy straight forward process.
                      </div>
                      <div class="mb-1">
                        A guide can be found here with instructions:
                        <a href="https://docs.google.com/document/d/1FpUN1E3bdWFxTbqrudFnxZC_togtFnx50pQ0z7zQS3M/edit?tab=t.0" target="_blank" class="text-decoration-underline">
                          Registration Guide
                        </a>
                      </div>
                      <div class="font-weight-bold">
                        Deadline: All team members are required to submit their COR within two (2) months of their date of employment.
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- Acknowledgement Receipt -->
                <v-col cols="12" md="6">
                  <v-file-input v-model="formData.receiptFile" label="Acknowledgement Receipt *" outlined dense
                    :rules="[rules.required]" prepend-icon="" prepend-inner-icon="mdi-receipt" show-size></v-file-input>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      You need to create your own A/R for <strong>DG Ventures LTD</strong> only indicating the actual invoice amount. This is just to acknowledge that you have received the same amount you have in your invoice. A/R with signature please.
                    </div>
                  </v-alert>
                </v-col>

                <!-- Tax Return Files (Multiple) -->
                <v-col cols="12">
                  <v-file-input 
                    v-model="formData.taxReturnFiles" 
                    :label="hasPriorTaxReturns ? 'Tax Return Files (Uploaded for this quarter)' : 'Tax Return Files *'" 
                    outlined 
                    dense
                    multiple 
                    accept=".pdf,.jpg,.jpeg,.png" 
                    prepend-icon="" 
                    prepend-inner-icon="mdi-file-document-multiple"
                    :rules="hasPriorTaxReturns ? [] : [rules.required]"
                    show-size 
                    counter>
                  </v-file-input>
                  
                  <v-alert
                    v-for="(qStatus, index) in remainingQuartersTaxReturnStatus"
                    :key="`${qStatus.year}-Q${qStatus.quarter}`"
                    :type="index === 0 ? qStatus.color : 'info'"
                    dense
                    text
                    class="mt-2 mb-2"
                  >
                    <div class="d-flex align-center">
                      <v-icon small :color="index === 0 ? qStatus.color : 'info'" class="mr-2">
                        {{ index === 0 ? (qStatus.status === 'uploaded' ? 'mdi-check-circle' : qStatus.status === 'overdue' ? 'mdi-alert-circle' : 'mdi-clock-outline') : 'mdi-information-outline' }}
                      </v-icon>
                      <span class="text-caption">{{ qStatus.message }}</span>
                    </div>
                  </v-alert>
                  
                  <v-alert type="info" dense text class="mt-2 mb-0">
                    <div class="text-caption">
                      <div class="mb-2">
                        <strong>Philippine VAs:</strong> Submit scanned copies of your Tax Returns for the 2025 tax year. These returns must correspond to the tax types listed in your COR (Certificate of Registration):
                      </div>
                      <ul class="pl-6 mb-2">
                        <li>Forms 2551Q, 1701Q, 1701A, or 1701</li>
                        <li>PDF copy of the BIR's email confirmation</li>
                        <li>Proof of payment / Tax receipts</li>
                      </ul>
                      <div class="mt-2">
                        <strong>South African VAs:</strong> Submit a scanned copy of your 2025 ITA34 (Notice of Assessment).
                      </div>
                    </div>
                  </v-alert>
                </v-col>

                <!-- Comments (Optional) -->
                <v-col cols="12">
                  <v-textarea v-model="formData.comments" label="Comments (Optional)" outlined dense rows="3"
                    prepend-inner-icon="mdi-comment-text-outline"
                    placeholder="Any additional notes or comments..."></v-textarea>
                </v-col>
              </v-row>
            </v-form>

            <v-divider class="my-4"></v-divider>
            
            <div class="d-flex justify-end">
              <v-btn text @click="resetForm" class="text-none mr-2">
                <v-icon left small>mdi-refresh</v-icon>
                Reset
              </v-btn>
              <v-btn color="primary" @click="submitForm" :disabled="!formValid || isSubmitting" :loading="isSubmitting" class="text-none">
                <v-icon left small>mdi-send</v-icon>
                Submit Payroll
              </v-btn>
            </div>
          </v-card-text>
        </v-tab-item>

        <!-- Tab 2: Submissions History -->
        <v-tab-item>
          <v-card-text>
            <div v-if="!mySubmissions || mySubmissions.length === 0" class="text-center py-8 grey--text">
              <v-icon size="64" color="grey lighten-1">mdi-inbox-outline</v-icon>
              <div class="mt-4 text-h6">No submissions yet</div>
              <div class="text-body-2">Your payroll submissions will appear here</div>
            </div>
            <v-row v-else class="submissions-wrapper">
              <v-col v-for="submission in mySubmissions" :key="submission.id" cols="12" md="6" lg="4">
            <v-card outlined class="submission-card">
              <v-card-title class="pb-1">
                <v-avatar size="36" color="primary" class="mr-2">
                  <span class="white--text text-body-2">
                    {{ getInitials(formData.name) }}
                  </span>
                </v-avatar>
                <div>
                  <div style="font-size: 0.95rem; font-weight: 600">
                    {{ formData.name }}
                  </div>
                  <div style="font-size: 0.75rem; color: #666; font-weight: 400;">
                    Invoice No: {{ submission.invoice_number || 'N/A' }}
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-chip small :color="getSubmissionStatusColor(submission.status)" text-color="white" label>
                  {{ submission.status }}
                </v-chip>
              </v-card-title>
              <v-card-subtitle class="pt-1">
                <v-icon x-small>mdi-domain</v-icon>
                {{ submission.company?.name || 'N/A' }}
              </v-card-subtitle>
              <v-divider></v-divider>
              <v-card-text class="py-2">
                <v-row dense>
                  <v-col cols="6">
                    <div class="detail-label">Period</div>
                    <div class="detail-value">
                      {{ formatDate(submission.invoice_period_from) }} - {{ formatDate(submission.invoice_period_to) }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="detail-label">Hours Worked</div>
                    <div class="detail-value">
                      {{ formatHours(submission.hours_worked) }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="detail-label">Invoice Amount</div>
                    <div class="detail-value">
                      {{ submission.currency || 'USD' }}
                      {{ formatCurrency(submission.invoice_amount) }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="detail-label">Submitted</div>
                    <div class="detail-value">
                      {{ formatDateTime(submission.submitted_at) }}
                    </div>
                  </v-col>
                </v-row>
                <div v-if="submission.comments" class="mt-2">
                  <div class="detail-label">Comments</div>
                  <div class="detail-value" style="font-style: italic">
                    "{{ submission.comments }}"
                  </div>
                </div>
                <div v-if="submission.status === 'denied' && submission.deny_reason" class="mt-2">
                  <div class="detail-label" style="color: #f44336">
                    Deny Reason
                  </div>
                  <div class="detail-value" style="color: #f44336">
                    {{ submission.deny_reason }}
                  </div>
                </div>
                <div v-if="submission.status === 'approved' && submission.approved_amount" class="mt-2">
                  <div class="detail-label" style="color: #4caf50">
                    Approved Amount
                  </div>
                  <div class="detail-value" style="color: #4caf50">
                    {{ submission.currency }}
                    {{ formatCurrency(submission.approved_amount) }}
                  </div>
                </div>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-chip v-if="submission.status === 'pending'" small color="warning" outlined>
                  <v-icon left small>mdi-clock-outline</v-icon>
                  Pending Review
                </v-chip>
                <v-chip v-else-if="submission.status === 'approved'" small color="success" outlined>
                  <v-icon left small>mdi-check-circle</v-icon>
                  Approved
                </v-chip>
                <v-chip v-else-if="submission.status === 'denied'" small color="error" outlined>
                  <v-icon left small>mdi-close-circle</v-icon>
                  Denied
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn icon small @click="viewSubmissionDetails(submission)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-tab-item>

    <!-- Tab 3: Exceptions -->
    <v-tab-item>
      <v-card-text>
        <!-- Exception Sub-tabs -->
        <v-tabs v-model="exceptionsSubTab" color="primary" class="mb-4">
          <v-tab>
            <v-icon left small>mdi-file-document-alert-outline</v-icon>
            Submission Exceptions
            <v-badge v-if="unresolvedSubmissionExceptions.length > 0" :content="unresolvedSubmissionExceptions.length" color="error" inline class="ml-1"></v-badge>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-briefcase-alert-outline</v-icon>
            PT Exceptions
            <v-badge v-if="unresolvedPtExceptions.length > 0" :content="unresolvedPtExceptions.length" color="error" inline class="ml-1"></v-badge>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="exceptionsSubTab">
          <!-- Submission Exceptions Sub-tab -->
          <v-tab-item>
            <v-progress-linear v-if="isLoadingExceptions" indeterminate color="primary" class="mb-4"></v-progress-linear>
            <div v-else>
              <div v-if="submissionExceptions.length === 0" class="text-center py-8 grey--text">
                <v-icon size="64" color="grey lighten-1">mdi-check-circle-outline</v-icon>
                <div class="mt-4 text-h6">No Exceptions</div>
                <div class="text-body-2">You have no payroll exceptions</div>
              </div>
              <v-alert
                v-for="exception in submissionExceptions"
                :key="exception.id"
                :type="getExceptionAlertType(exception.severity)"
                :icon="getExceptionIcon(exception.severity)"
                outlined
                dense
                class="mb-3"
              >
                <div class="d-flex justify-space-between align-start">
                  <div>
                    <div class="font-weight-bold text-body-2">
                      {{ formatExceptionType(exception.exception_type) }}
                      <v-chip v-if="exception.is_resolved" x-small color="success" outlined class="ml-2">
                        Resolved
                      </v-chip>
                    </div>
                    <div class="text-body-2 mt-1">{{ exception.description }}</div>
                    <div class="text-caption grey--text mt-1">
                      Invoice: {{ exception.submission?.invoice_number || 'N/A' }} | 
                      Detected: {{ formatDate(exception.detected_at) }}
                    </div>
                    <div v-if="exception.is_resolved" class="text-caption success--text mt-1">
                      Resolved by {{ exception.resolver?.name || 'Unknown' }} on {{ formatDate(exception.resolved_at) }}
                      <span v-if="exception.resolution_notes"> - {{ exception.resolution_notes }}</span>
                    </div>
                  </div>
                </div>
              </v-alert>
            </div>
          </v-tab-item>

          <!-- PT Exceptions Sub-tab -->
          <v-tab-item>
            <v-progress-linear v-if="isLoadingPtExceptions" indeterminate color="primary" class="mb-4"></v-progress-linear>
            <div v-else>
              <div v-if="ptExceptions.length === 0" class="text-center py-8 grey--text">
                <v-icon size="64" color="grey lighten-1">mdi-check-circle-outline</v-icon>
                <div class="mt-4 text-h6">No PT Exceptions</div>
                <div class="text-body-2">You have no PT payroll exceptions</div>
              </div>
              <v-alert
                v-for="exception in ptExceptions"
                :key="exception.id"
                :type="getExceptionAlertType(exception.severity)"
                :icon="getExceptionIcon(exception.severity)"
                outlined
                dense
                class="mb-3"
              >
                <div class="d-flex justify-space-between align-start">
                  <div>
                    <div class="font-weight-bold text-body-2">
                      {{ formatExceptionType(exception.exception_type) }}
                      <v-chip v-if="exception.is_resolved" x-small color="success" outlined class="ml-2">
                        Resolved
                      </v-chip>
                    </div>
                    <div class="text-body-2 mt-1">{{ exception.description }}</div>
                    <div class="text-caption grey--text mt-1">
                      Invoice: {{ exception.pt_submission?.invoice_number || 'N/A' }} | 
                      Detected: {{ formatDate(exception.detected_at) }}
                    </div>
                    <div v-if="exception.is_resolved" class="text-caption success--text mt-1">
                      Resolved by {{ exception.resolver?.name || 'Unknown' }} on {{ formatDate(exception.resolved_at) }}
                      <span v-if="exception.resolution_notes"> - {{ exception.resolution_notes }}</span>
                    </div>
                  </div>
                </div>
              </v-alert>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-tab-item>
  </v-tabs-items>
</v-card>

    <!-- View Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedSubmission">
        <v-card-title>
          <v-icon left>mdi-file-document</v-icon>
          Submission Details
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-simple-table>
            <tbody>
              <tr>
                <td style="font-weight: 600; width: 40%">Employee</td>
                <td>{{ formData.name }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Email</td>
                <td>{{ formData.email }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice Number</td>
                <td>{{ selectedSubmission.invoice_number }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Company</td>
                <td>{{ selectedSubmission.company?.name || 'N/A' }}</td>
              </tr>
              <tr>
                <td style="font-weight: 600">Service Period</td>
                <td>
                  {{ formatDate(selectedSubmission.invoice_period_from) }} - {{ formatDate(selectedSubmission.invoice_period_to) }}
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Hours Worked</td>
                <td>
                  {{ formatHours(selectedSubmission.hours_worked) }}
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice Amount</td>
                <td>
                  {{ selectedSubmission.currency }}
                  {{ formatCurrency(selectedSubmission.invoice_amount) }}
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Status</td>
                <td>
                  <v-chip small :color="getSubmissionStatusColor(selectedSubmission.status)" text-color="white" label>
                    {{ selectedSubmission.status }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Submitted</td>
                <td>{{ formatDateTime(selectedSubmission.submitted_at) }}</td>
              </tr>
              <tr v-if="selectedSubmission.approved_amount">
                <td style="font-weight: 600">Approved Amount</td>
                <td>
                  <span style="color: #4caf50; font-weight: 600;">
                    {{ selectedSubmission.currency }} {{ formatCurrency(selectedSubmission.approved_amount) }}
                  </span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Invoice File</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.invoice_file_path">
                    <v-icon left x-small>mdi-file-pdf-box</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.invoice_file_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.invoice_file_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">COR File</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.cor_file_path">
                    <v-icon left x-small>mdi-certificate</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.cor_file_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.cor_file_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Acknowledgement Receipt</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.acknowledgement_receipt_path">
                    <v-icon left x-small>mdi-receipt-text</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.acknowledgement_receipt_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.acknowledgement_receipt_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Tax Return Files</td>
                <td>
                  <div v-if="selectedSubmission.tax_return_urls && selectedSubmission.tax_return_urls.length > 0">
                    <v-chip small outlined v-for="(taxFile, index) in selectedSubmission.tax_return_urls" :key="index" class="mr-1 mb-1">
                      <v-icon left x-small>mdi-file-document</v-icon>
                      <a :href="`https://api.dgventureslimited.com/storage/${taxFile.path}`" target="_blank" style="text-decoration: none; color: inherit;">
                        {{ taxFile.filename }}
                      </a>
                    </v-chip>
                  </div>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr>
                <td style="font-weight: 600">Payment Receipt</td>
                <td>
                  <v-chip small outlined v-if="selectedSubmission.payment_receipt_path">
                    <v-icon left x-small>mdi-receipt</v-icon>
                    <a :href="`https://api.dgventureslimited.com/storage/${selectedSubmission.payment_receipt_path}`" target="_blank" style="text-decoration: none; color: inherit;">
                      {{ selectedSubmission.payment_receipt_path.split('/').pop() }}
                    </a>
                  </v-chip>
                  <span v-else class="grey--text">Not uploaded</span>
                </td>
              </tr>
              <tr v-if="selectedSubmission.comments">
                <td style="font-weight: 600">Comments</td>
                <td>{{ selectedSubmission.comments }}</td>
              </tr>
              <tr v-if="selectedSubmission.deny_reason">
                <td style="font-weight: 600; color: #f44336">Deny Reason</td>
                <td style="color: #f44336">{{ selectedSubmission.deny_reason }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "EmployeePayrollView",
  props: {
    user: {
      type: Object,
      required: true,
    },
    companies: {
      type: Array,
      default: () => [],
    },
    mySubmissions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: 0,
      formValid: false,
      isSubmitting: false,
      submissionSuccess: false,
      dateRangeMenu: false,
      timeDoctorDateMenu: false,
      currencies: ["USD", "PHP", "EUR", "GBP"],
      // Time Doctor authorization
      timeDoctorAuthorized: false,
      isCheckingAuth: false,
      // Time Doctor user
      timeDoctorUserId: null,
      timeDoctorCompanyId: 41090,
      isLoadingTimeDoctorUser: false,
      // Time Doctor data
      timeDoctorDateRange: [],
      timeDoctorData: null,
      timeDoctorError: null,
      isLoadingTimeDoctorData: false,
      // Submission details dialog
      detailsDialog: false,
      selectedSubmission: null,
      // Exceptions
      exceptionsSubTab: 0,
      submissionExceptions: [],
      ptExceptions: [],
      isLoadingExceptions: false,
      isLoadingPtExceptions: false,
      // Form data
      formData: {
        email: "",
        name: "",
        invoicePeriod: [],
        company: null,
        invoiceNumber: "",
        hoursWorked: {
          hours: null,
          minutes: null,
        },
        currency: "USD",
        invoiceAmount: null,
        invoiceFile: null,
        corFile: null,
        receiptFile: null,
        taxReturnFiles: [],
        comments: "",
      },
      rules: {
        required: (v) => !!v || v === 0 || "This field is required",
        email: (v) =>
          !v || /.+@.+\..+/.test(v) || "Please enter a valid email",
        positiveNumber: (v) =>
          v === null || v === "" || v >= 0 || "Must be a positive number",
        minutes: (v) =>
          v === null ||
          v === "" ||
          (v >= 0 && v <= 59) ||
          "Minutes must be between 0-59",
        pdfFile: (v) =>
          !v ||
          v.type === "application/pdf" ||
          "Please upload a PDF file",
      },
    };
  },
  computed: {
    formattedDateRange() {
      if (
        !this.formData.invoicePeriod ||
        this.formData.invoicePeriod.length === 0
      ) {
        return "";
      }
      if (this.formData.invoicePeriod.length === 1) {
        return this.formatDate(this.formData.invoicePeriod[0]);
      }
      const sorted = [...this.formData.invoicePeriod].sort();
      return `${this.formatDate(sorted[0])} - ${this.formatDate(sorted[1])}`;
    },
    formattedTimeDoctorDateRange() {
      if (!this.timeDoctorDateRange || this.timeDoctorDateRange.length === 0) {
        return "";
      }
      if (this.timeDoctorDateRange.length === 1) {
        return this.formatDate(this.timeDoctorDateRange[0]);
      }
      const sorted = [...this.timeDoctorDateRange].sort();
      return `${this.formatDate(sorted[0])} - ${this.formatDate(sorted[1])}`;
    },
    totalTimeDoctorHours() {
      if (this.timeDoctorData && this.timeDoctorData.total_formatted) {
        return this.timeDoctorData.total_formatted;
      }
      return "0h 0m";
    },
    /**
     * Get Time Doctor hours as decimal value
     */
    timeDoctorHoursDecimal() {
      if (!this.timeDoctorData) return 0;
      return (this.timeDoctorData.total_hours || 0) + (this.timeDoctorData.total_minutes || 0) / 60;
    },
    /**
     * Get submitted hours as decimal value
     */
    submittedHoursDecimal() {
      const hours = this.formData.hoursWorked.hours || 0;
      const minutes = this.formData.hoursWorked.minutes || 0;
      return hours + minutes / 60;
    },
    /**
     * Calculate the expected amount based on hourly rate and submitted hours
     */
    calculatedAmount() {
      const hourlyRate = this.user?.hourly_rate;
      if (!hourlyRate || hourlyRate <= 0) return null;
      return this.submittedHoursDecimal * hourlyRate;
    },
    /**
     * Format the calculated amount with currency
     */
    calculatedAmountFormatted() {
      if (this.calculatedAmount === null) return null;
      return `${this.formData.currency} ${this.formatCurrency(this.calculatedAmount)}`;
    },
    /**
     * Check if hours were overridden from Time Doctor hours
     */
    hoursOverridden() {
      if (!this.timeDoctorData || this.timeDoctorHoursDecimal === 0) return false;
      // Check if there's more than 1 minute difference
      return Math.abs(this.submittedHoursDecimal - this.timeDoctorHoursDecimal) > (1 / 60);
    },
    /**
     * Calculate hours variance percentage
     */
    hoursVariancePercent() {
      if (!this.timeDoctorData || this.timeDoctorHoursDecimal === 0) return 0;
      return ((this.submittedHoursDecimal - this.timeDoctorHoursDecimal) / this.timeDoctorHoursDecimal) * 100;
    },
    /**
     * Check if invoice amount differs from calculated amount
     */
    amountMismatch() {
      if (this.calculatedAmount === null || !this.formData.invoiceAmount) return false;
      // Allow 2% variance
      const variance = Math.abs(this.formData.invoiceAmount - this.calculatedAmount) / this.calculatedAmount * 100;
      return variance > 2;
    },
    /**
     * Calculate amount variance percentage
     */
    amountVariancePercent() {
      if (this.calculatedAmount === null || this.calculatedAmount === 0) return 0;
      return ((this.formData.invoiceAmount - this.calculatedAmount) / this.calculatedAmount) * 100;
    },
    /**
     * Filter unresolved submission exceptions
     */
    unresolvedSubmissionExceptions() {
      return this.submissionExceptions.filter(e => !e.is_resolved);
    },
    /**
     * Filter unresolved PT exceptions
     */
    unresolvedPtExceptions() {
      return this.ptExceptions.filter(e => !e.is_resolved);
    },
    /**
     * Total unresolved exceptions count for badge
     */
    totalExceptionsCount() {
      return this.unresolvedSubmissionExceptions.length + this.unresolvedPtExceptions.length;
    },
    /**
     * Check if user has COR already on file from a previous submission (one-time requirement)
     */
    hasPriorCor() {
      return this.mySubmissions && this.mySubmissions.some(s => s.cor_file_path);
    },
    /**
     * Get current quarter info
     */
    currentQuarter() {
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      const quarter = Math.floor(month / 3) + 1;
      return { quarter, year };
    },
    /**
     * Check if tax returns are required for current quarter
     * Based on whether user has uploaded tax returns in current quarter
     */
    hasPriorTaxReturns() {
      return this.hasUploadedTaxReturnsForQuarter(this.currentQuarter.quarter, this.currentQuarter.year);
    },
    /**
     * Status for every quarter this year whose due date has not passed yet.
     * A quarter's filing window stays open past the end of that calendar
     * quarter (e.g. Q2 is due Aug 15), so this checks due dates directly
     * rather than only looking at quarters >= the current calendar quarter.
     */
    remainingQuartersTaxReturnStatus() {
      const { year } = this.currentQuarter;
      const now = new Date();
      const statuses = [];

      for (let quarter = 1; quarter <= 4; quarter++) {
        const dueDate = this.getTaxReturnDueDate(quarter, year);
        if (now > dueDate) continue;

        statuses.push(this.getTaxReturnStatus(quarter, year, dueDate, now));
      }

      return statuses;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.formData.email = newUser.email || "";
          this.formData.name = this.getUserName(newUser);
          this.formData.currency = newUser.payment_currency || "USD";
        }
      },
    },
    timeDoctorData: {
      handler(newData) {
        if (newData && newData.total_formatted) {
          // Parse hours and minutes from Time Doctor data
          const parsed = this.parseTimeDoctorHours(newData.total_formatted);
          if (parsed) {
            this.formData.hoursWorked.hours = parsed.hours;
            this.formData.hoursWorked.minutes = parsed.minutes;
          }
        }
      },
    },
  },
  mounted() {
    // Check for OAuth callback code in URL
    this.handleOAuthCallback();
    // Check Time Doctor authorization status
    this.checkTimeDoctorAuth();
    // Fetch exceptions
    this.fetchExceptions();
    this.fetchPtExceptions();
  },
  methods: {
    sourceLabel(source) {
      return { classic: 'Classic', td2: 'TD2', dgtasks: 'DGTasks' }[source] || source;
    },
    sourceColor(source) {
      return { td2: 'green lighten-4', dgtasks: 'blue lighten-4' }[source] || 'grey lighten-3';
    },
    /**
     * Due date for a given tax quarter (Q1->May 15, Q2->Aug 15, Q3->Nov 15, Q4->April 15 next year)
     */
    getTaxReturnDueDate(quarter, year) {
      const dueDates = {
        1: new Date(year, 4, 15),
        2: new Date(year, 7, 15),
        3: new Date(year, 10, 15),
        4: new Date(year + 1, 3, 15),
      };
      return dueDates[quarter];
    },
    /**
     * Check if the user has uploaded tax returns for a given quarter/year
     */
    hasUploadedTaxReturnsForQuarter(quarter, year) {
      if (!this.mySubmissions || this.mySubmissions.length === 0) return false;

      return this.mySubmissions.some(s => {
        if (!s.tax_return_urls || s.tax_return_urls.length === 0) return false;
        const d = new Date(s.created_at);
        const sQuarter = Math.floor(d.getMonth() / 3) + 1;
        return sQuarter === quarter && d.getFullYear() === year;
      });
    },
    /**
     * Get tax return status object for display for a given quarter
     */
    getTaxReturnStatus(quarter, year, dueDate, now) {
      if (this.hasUploadedTaxReturnsForQuarter(quarter, year)) {
        return {
          quarter,
          year,
          status: 'uploaded',
          message: `Tax returns submitted for Q${quarter} ${year} (due by ${this.$date(dueDate).format('MMM D, YYYY')})`,
          color: 'success'
        };
      } else if (now > dueDate) {
        return {
          quarter,
          year,
          status: 'overdue',
          message: `Tax returns overdue for Q${quarter} ${year} (was due ${this.$date(dueDate).format('MMM D, YYYY')})`,
          color: 'error'
        };
      } else {
        return {
          quarter,
          year,
          status: 'pending',
          message: `Tax returns due by ${this.$date(dueDate).format('MMM D, YYYY')} for Q${quarter} ${year}`,
          color: 'warning'
        };
      }
    },
    /**
     * Check if we have a valid Time Doctor authorization.
     */
    async checkTimeDoctorAuth() {
      this.isCheckingAuth = true;
      try {
        const response = await this.$axios.get('/api/payroll/time-doctor/auth-status');
        console.log('Time Doctor auth status:', response.data);
        
        if (response.data.response && response.data.data) {
          // Hours include DGTasks, which needs no Time Doctor connection, so the panel always shows
          this.timeDoctorAuthorized = true;
          
          // If authorized, fetch user ID then set default payroll period
          if (this.timeDoctorAuthorized) {
            await this.fetchTimeDoctorUserId();
            this.setCurrentPayrollPeriod();
          }
        }
      } catch (error) {
        console.error('Error checking Time Doctor auth:', error);
        this.timeDoctorAuthorized = false;
      } finally {
        this.isCheckingAuth = false;
      }
    },
    
    /**
     * Handle OAuth callback - check URL for auth code (admin redirected here after auth)
     */
    async handleOAuthCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        console.log('OAuth callback detected, exchanging code for token...');
        try {
          const response = await this.$axios.get('/api/payroll/time-doctor/callback', {
            params: { code }
          });
          
          console.log('OAuth callback response:', response.data);
          
          if (response.data && response.data.response) {
            this.$toast.success('Time Doctor connected successfully!');
            this.timeDoctorAuthorized = true;
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Fetch user ID then data now that we're authorized
            await this.fetchTimeDoctorUserId();
            this.setCurrentPayrollPeriod();
          } else {
            this.$toast.error(response.data?.message || 'Failed to connect Time Doctor');
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          this.$toast.error(error.response?.data?.message || 'Failed to connect Time Doctor');
        }
      }
    },
    
    /**
     * Calculate the current two-week payroll period.
     * Payroll periods are fixed two-week cycles (Saturday to Friday).
     * Example: If today is Mar 21, 2026, the current period is Mar 21 - Apr 3, 2026.
     */
    calculatePayrollPeriod(date) {
      const today = this.$date(date);
      
      // Define a known payroll period start date (anchor date)
      // We use Jan 12, 2026 - this is a Monday, giving Monday-to-Sunday periods
      const anchorDate = this.$date('2026-01-12');
      
      // Calculate days since anchor
      const daysSinceAnchor = today.diff(anchorDate, 'day');
      
      // Calculate which period we're in (14-day cycles)
      const periodNumber = Math.floor(daysSinceAnchor / 14);
      
      // Calculate start and end of current period
      const periodStart = anchorDate.add(periodNumber * 14, 'day');
      const periodEnd = periodStart.add(13, 'day'); // 14 days total (0-13)
      
      return {
        start: periodStart.format('YYYY-MM-DD'),
        end: periodEnd.format('YYYY-MM-DD'),
      };
    },
    setCurrentPayrollPeriod() {
      const today = this.$date();
      const period = this.calculatePayrollPeriod(new Date());
      // Sat/Sun already resolve to the period that's wrapping up that weekend
      // (periods run Mon-Sun), so only the Monday that starts a brand new
      // period needs special-casing: default to the period that just ended,
      // since employees typically submit on that first Monday for it.
      // A "middle" Monday one week into the current period should NOT trigger this.
      if (today.day() === 1 && today.format('YYYY-MM-DD') === period.start) {
        this.setPreviousPayrollPeriod();
        return;
      }
      this.timeDoctorDateRange = [period.start, period.end];
      // Also set invoice period to match by default
      this.formData.invoicePeriod = [period.start, period.end];
      this.fetchTimeDoctorData();
    },
    setPreviousPayrollPeriod() {
      const currentPeriod = this.calculatePayrollPeriod(new Date());
      const previousStart = this.$date(currentPeriod.start).subtract(14, 'day');
      const previousEnd = previousStart.add(13, 'day');
      const periodDates = [
        previousStart.format('YYYY-MM-DD'),
        previousEnd.format('YYYY-MM-DD'),
      ];
      this.timeDoctorDateRange = periodDates;
      // Also set invoice period to match by default
      this.formData.invoicePeriod = periodDates;
      this.fetchTimeDoctorData();
    },
    onInvoicePeriodChange() {
      this.dateRangeMenu = false;
      this.timeDoctorDateRange = [...this.formData.invoicePeriod];
      if (this.formData.invoicePeriod.length === 2) {
        this.fetchTimeDoctorData();
      }
    },
    onTimeDoctorDateChange() {
      this.timeDoctorDateMenu = false;
      this.formData.invoicePeriod = [...this.timeDoctorDateRange];
      if (this.timeDoctorDateRange.length === 2) {
        this.fetchTimeDoctorData();
      }
    },
    clearTimeDoctorDateRange() {
      this.timeDoctorDateRange = [];
      this.formData.invoicePeriod = [];
      this.timeDoctorData = null;
      this.timeDoctorError = null;
    },
    /**
     * Fetch the Time Doctor user ID for the current user's email
     */
    async fetchTimeDoctorUserId() {
      if (!this.user?.email) {
        console.warn('No user email available to fetch Time Doctor user ID');
        return;
      }

      // Check if user already has cached Time Doctor user ID
      if (this.user.time_doctor_user_id) {
        this.timeDoctorUserId = this.user.time_doctor_user_id;
        console.log('Using cached Time Doctor user ID:', this.timeDoctorUserId);
        return;
      }

      // If not cached, sync from Time Doctor API
      this.isLoadingTimeDoctorUser = true;
      this.timeDoctorError = null;

      try {
        console.log('Syncing Time Doctor user ID for email:', this.user.email);
        
        const response = await this.$axios.post('/api/payroll/time-doctor/sync-user-id');

        console.log('Time Doctor sync response:', response.data);

        if (response.data.response && response.data.data?.time_doctor_user_id) {
          this.timeDoctorUserId = response.data.data.time_doctor_user_id;
          console.log('Time Doctor user ID synced and cached:', this.timeDoctorUserId);
          this.$toast.success('Time Doctor user linked successfully');
        } else {
          console.warn('Time Doctor sync returned no user ID');
          this.timeDoctorError = 'Unable to sync Time Doctor user. Please try again.';
        }
      } catch (error) {
        console.error('Error syncing Time Doctor user:', error);
        const errorMessage = error.response?.data?.message || 'Please go to Settings in the upper right corner, click Edit Profile, and enter your Time Doctor email as your Secondary Email, then refresh this page.';
        this.timeDoctorError = errorMessage;
        // Not fatal: TD2 / DGTasks hours still load without a Classic id
      } finally {
        this.isLoadingTimeDoctorUser = false;
      }
    },
    async fetchTimeDoctorData() {
      if (!this.timeDoctorDateRange || this.timeDoctorDateRange.length < 2) {
        this.$toast.warning('Please select a date range first');
        return;
      }

      const sorted = [...this.timeDoctorDateRange].sort();
      const from = sorted[0];
      const to = sorted[1];

      this.isLoadingTimeDoctorData = true;
      this.timeDoctorError = null;

      try {
        console.log('Fetching Time Doctor data for period:', from, 'to', to, 'user_id:', this.timeDoctorUserId);
        
        const response = await this.$axios.get('/api/payroll/time-doctor/hours', {
          params: {
            from: from,
            to: to,
            company_id: this.timeDoctorCompanyId,
            user_id: this.timeDoctorUserId || undefined,
          },
        });

        console.log('Time Doctor API response:', response.data);

        if (response.data.response && response.data.data) {
          this.timeDoctorData = response.data.data;
          console.log('Parsed Time Doctor data:', this.timeDoctorData);
        } else {
          this.timeDoctorError = response.data.message || 'Failed to fetch Time Doctor data';
        }
      } catch (error) {
        console.error('Error fetching Time Doctor data:', error);
        this.timeDoctorError = error.response?.data?.message || error.message || 'Failed to fetch Time Doctor data';
      } finally {
        this.isLoadingTimeDoctorData = false;
      }
    },
    /**
     * Parse Time Doctor formatted time string (e.g., "8h 30m") into hours and minutes
     */
    parseTimeDoctorHours(formattedTime) {
      if (!formattedTime) return null;
      
      // Match patterns like "8h 30m", "10h 0m", "0h 45m"
      const match = formattedTime.match(/(\d+)h\s*(\d+)m/);
      if (match) {
        return {
          hours: parseInt(match[1], 10),
          minutes: parseInt(match[2], 10),
        };
      }
      
      return null;
    },
    getUserName(user) {
      if (user.name) {
        return user.name;
      }
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || "Unknown";
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return this.$date(dateStr).format("MMM D, YYYY");
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      return this.$date(dateStr).format("MMM D, YYYY h:mm A");
    },
    submitForm() {
      this.isSubmitting = true;
      if (this.$refs.payrollForm.validate()) {
        const sortedPeriod = [...this.formData.invoicePeriod].sort();
        const payload = {
          email: this.formData.email,
          name: this.formData.name,
          invoicePeriod: {
            from: sortedPeriod[0] || null,
            to: sortedPeriod[1] || sortedPeriod[0] || null,
          },
          company: this.formData.company
            ? {
              id: this.formData.company.id,
              name: this.formData.company.name,
            }
            : null,
          invoiceNumber: this.formData.invoiceNumber,
          hoursWorked: {
            hours: this.formData.hoursWorked.hours,
            minutes: this.formData.hoursWorked.minutes,
          },
          currency: this.formData.currency,
          invoiceAmount: this.formData.invoiceAmount,
          // Time Doctor data for validation
          timeDoctorHours: this.timeDoctorData ? this.timeDoctorHoursDecimal : null,
          calculatedAmount: this.calculatedAmount,
          hourlyRate: this.user?.hourly_rate || null,
          // Pass actual file objects (not metadata)
          invoiceFile: this.formData.invoiceFile,
          corFile: this.formData.corFile,
          receiptFile: this.formData.receiptFile,
          taxReturnFiles: this.formData.taxReturnFiles || [],
          comments: this.formData.comments || null,
          submittedAt: new Date().toISOString(),
        };

        this.$emit("submit", payload);
      } else {
        this.isSubmitting = false;
      }
    },
    showSubmissionSuccess() {
      this.isSubmitting = false;
      this.submissionSuccess = true;
      this.resetForm();
    },
    enableSubmit() {
      this.isSubmitting = false;
    },
    startNewSubmission() {
      this.submissionSuccess = false;
    },
    getSubmissionStatusColor(status) {
      const colors = {
        pending: 'orange',
        approved: 'success',
        denied: 'error',
      };
      return colors[status] || 'grey';
    },
    formatHours(decimal) {
      if (!decimal && decimal !== 0) return '0h';
      const num = parseFloat(decimal);
      const hours = Math.floor(num);
      const minutes = Math.round((num - hours) * 60);
      if (minutes === 0) return `${hours}h`;
      return `${hours}h ${minutes}m`;
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '-';
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    /**
     * Fetch user's regular payroll exceptions
     */
    async fetchExceptions() {
      this.isLoadingExceptions = true;
      try {
        const response = await this.$axios.get('/api/payroll/my-exceptions');
        if (response.data.response) {
          this.submissionExceptions = response.data.data || [];
        }
      } catch (error) {
        console.error('Error fetching exceptions:', error);
        this.$toast.error('Failed to load exceptions');
      } finally {
        this.isLoadingExceptions = false;
      }
    },
    /**
     * Fetch user's PT payroll exceptions
     */
    async fetchPtExceptions() {
      this.isLoadingPtExceptions = true;
      try {
        const response = await this.$axios.get('/api/payroll/pt/my-exceptions');
        if (response.data.response) {
          this.ptExceptions = response.data.data || [];
        }
      } catch (error) {
        console.error('Error fetching PT exceptions:', error);
        this.$toast.error('Failed to load PT exceptions');
      } finally {
        this.isLoadingPtExceptions = false;
      }
    },
    /**
     * Get exception alert type based on severity
     */
    getExceptionAlertType(severity) {
      const types = {
        info: 'info',
        warning: 'warning',
        error: 'error',
      };
      return types[severity] || 'info';
    },
    /**
     * Get exception icon based on severity
     */
    getExceptionIcon(severity) {
      const icons = {
        info: 'mdi-information',
        warning: 'mdi-alert',
        error: 'mdi-alert-circle',
      };
      return icons[severity] || 'mdi-information';
    },
    /**
     * Format exception type for display
     */
    formatExceptionType(type) {
      const types = {
        hours_mismatch: 'Hours Mismatch',
        rate_discrepancy: 'Rate Discrepancy',
        missing_documents: 'Missing Documents',
        cor_expired: 'COR Expired',
        duplicate_invoice: 'Duplicate Invoice',
        excessive_hours: 'Excessive Hours',
        other: 'Other',
      };
      return types[type] || type;
    },
    resetForm() {
      this.formData = {
        email: this.user?.email || "",
        name: this.getUserName(this.user || {}),
        invoicePeriod: [],
        company: null,
        invoiceNumber: "",
        hoursWorked: {
          hours: null,
          minutes: null,
        },
        currency: this.user?.payment_currency || "USD",
        invoiceAmount: null,
        invoiceFile: null,
        corFile: null,
        receiptFile: null,
        taxReturnFiles: [],
        comments: "",
      };
      this.$refs.payrollForm?.resetValidation();
    },
    clearDateRange() {
      this.formData.invoicePeriod = [];
      this.timeDoctorDateRange = [];
      this.timeDoctorData = null;
      this.timeDoctorError = null;
    },
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
    viewSubmissionDetails(submission) {
      this.selectedSubmission = submission;
      this.detailsDialog = true;
    },
  },
};
</script>

<style scoped>
.v-text-field--readonly>>>.v-input__slot {
  background-color: #f5f5f5 !important;
}

.submissions-wrapper {
  overflow-y: auto;
  max-height: 600px;
}

.submission-card {
  transition: all 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.submission-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.submission-card .v-card__text {
  overflow-y: auto;
  max-height: 300px;
  flex: 1;
}

.detail-label {
  font-size: 0.7rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
}

.detail-value {
  font-size: 0.85rem;
  font-weight: 500;
}
</style>
